import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useBeneficiaries, useDeleteBeneficiary, type Beneficiary } from './api.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';

function initials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

function subtitleFor(b: Beneficiary, t: (key: string) => string) {
  const details = b.payout_details as { phone_number?: string; iban?: string };
  const value = b.payout_method === 'bank' ? details.iban : details.phone_number;
  const method = t(`beneficiaries.${b.payout_method === 'bank' ? 'bank' : 'mobileMoney'}`);
  return `${value ?? '—'} (${method})`;
}

export function BeneficiariesListPage() {
  const { t } = useTranslation();
  const beneficiariesQ = useBeneficiaries();
  const del = useDeleteBeneficiary();
  const [query, setQuery] = useState('');

  const items = useMemo(() => {
    const all = beneficiariesQ.data?.items ?? [];
    const lowered = query.trim().toLowerCase();
    if (!lowered) return all;
    return all.filter((item) => {
      const details = item.payout_details as { phone_number?: string; iban?: string };
      return (
        item.full_name.toLowerCase().includes(lowered) ||
        String(details.phone_number ?? details.iban ?? '')
          .toLowerCase()
          .includes(lowered)
      );
    });
  }, [beneficiariesQ.data?.items, query]);

  if (beneficiariesQ.isLoading) return <LoadingScreen />;
  if (beneficiariesQ.isError) return <ErrorState onRetry={() => beneficiariesQ.refetch()} />;
  if (!beneficiariesQ.data?.items.length) {
    return (
      <div className="mx-auto max-w-3xl space-y-8">
        <EmptyState title={t('beneficiaries.empty')} />
        <Link to="/beneficiaries/new" className="mx-auto flex w-fit items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-3xl text-white">
            +
          </div>
          <span className="text-3xl font-medium text-text">{t('beneficiaries.addCta')}</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div className="space-y-5">
        <Link
          to="/app"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-elevated text-2xl text-text"
        >
          ←
        </Link>
        <div className="space-y-2">
          <h1 className="text-5xl font-semibold tracking-tight text-primary">
            {t('beneficiaries.chooseRecipient')}
          </h1>
          <div className="rounded-[2rem] border border-border px-5 py-4">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('beneficiaries.searchPlaceholder')}
              className="w-full bg-transparent text-lg text-text outline-none placeholder:text-text-muted"
            />
          </div>
        </div>
      </div>

      <Link to="/beneficiaries/new" className="flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-3xl text-white">
          +
        </div>
        <span className="text-3xl font-medium text-text">{t('beneficiaries.addCta')}</span>
      </Link>

      <section className="space-y-4">
        <h2 className="border-b border-border pb-4 text-2xl font-medium text-primary">
          {t('beneficiaries.savedRecipients')}
        </h2>
        <ul className="flex flex-col divide-y divide-border">
          {items.map((beneficiary) => (
            <li key={beneficiary.id} className="flex items-center gap-4 py-5">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-elevated text-2xl text-text">
                {initials(beneficiary.full_name)}
              </div>
              <div className="min-w-0 flex-1">
                <Link to={`/beneficiaries/${beneficiary.id}/edit`} className="block">
                  <p className="truncate text-2xl font-medium text-text">{beneficiary.full_name}</p>
                  <p className="truncate text-lg text-text-muted">{subtitleFor(beneficiary, t)}</p>
                </Link>
              </div>
              <button
                type="button"
                onClick={() => del.mutate(beneficiary.id)}
                className="text-sm font-medium uppercase tracking-[0.12em] text-text-muted"
              >
                {t('common.delete')}
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
